import { useEditorContext } from "../EditorContext";

import { FORMAT_TEXT_COMMAND } from "lexical";
import { $isListNode } from "@lexical/list";
import {
	INSERT_UNORDERED_LIST_COMMAND,
	INSERT_ORDERED_LIST_COMMAND,
} from "@lexical/list";

import { useCallback, useState, useEffect } from "react";
import { $getSelection, $isRangeSelection } from "lexical";

function ToolbarButton({ active, children, ...props }) {
	const fallback = props.fallback || false;
	return (
		<button
			{...props}
			className={`flex items-center justify-center transition-all bg-transparent text-grey hover:text-grey-text ${active ? "!text-primary" : ""}`}
		>
			{
				fallback ? <span className="font-MaterialIconsNew text-[28px] font-thin">{children}</span> : <span className="fk-icons text-base">{children}</span>
			}

		</button>
	);
}

function ToolbarDivider({ height = "h-5" }) {
	return <div className={`border-l ${height} self-center`}></div>;
}

// DRY: Extract toolbar buttons to a reusable component
function ToolbarButtons({ formatState, applyFormat }) {
	return (
		<>
			<ToolbarButton
				active={formatState.bold}
				onClick={() => applyFormat(FORMAT_TEXT_COMMAND, "bold")}
			>
				bold
			</ToolbarButton>
			<ToolbarButton
				active={formatState.italic}
				onClick={() => applyFormat(FORMAT_TEXT_COMMAND, "italic")}
			>
				italic
			</ToolbarButton>
			<ToolbarButton
				active={formatState.underline}
				onClick={() => applyFormat(FORMAT_TEXT_COMMAND, "underline")}
			>
				underline
			</ToolbarButton>
			<ToolbarButton
				active={formatState.strikethrough}
				onClick={() => applyFormat(FORMAT_TEXT_COMMAND, "strikethrough")}
			>
				line-through
			</ToolbarButton>
			<ToolbarDivider />
			<ToolbarButton
				active={formatState.orderedList}
				onClick={() => applyFormat(INSERT_ORDERED_LIST_COMMAND)}
			>
				ordered-list
			</ToolbarButton>
			<ToolbarButton
				active={formatState.unorderedList}
				onClick={() => applyFormat(INSERT_UNORDERED_LIST_COMMAND)}
			>
				unordered-list
			</ToolbarButton>
		</>
	);
}

export function TopToolbar() {
	const { editor, showTopToolbar } = useEditorContext();
	const [formatState, setFormatState] = useState({
		bold: false,
		italic: false,
		underline: false,
		strikethrough: false,
		orderedList: false,
		unorderedList: false,
	});

	const applyFormat = useCallback((command, payload) => {
		editor.dispatchCommand(command, payload);
	}, [editor]);

	useEffect(() => {
		const unregister = editor.registerUpdateListener(({ editorState }) => {
			editorState.read(() => {
				const selection = $getSelection();
				let orderedList = false;
				let unorderedList = false;

				if ($isRangeSelection(selection)) {
					const anchor = selection.anchor;
					const node = anchor.getNode();
					let parent = node.getParent();

					while (parent) {
						if ($isListNode(parent)) {
							if (parent.getListType() === "number") {
								orderedList = true;
							}
							if (parent.getListType() === "bullet") {
								unorderedList = true;
							}
							break;
						}
						parent = parent.getParent();
					}

					setFormatState((prev) => ({
						...prev,
						bold: selection.hasFormat("bold"),
						italic: selection.hasFormat("italic"),
						underline: selection.hasFormat("underline"),
						strikethrough: selection.hasFormat("strikethrough"),
						orderedList,
						unorderedList,
					}));
				}
			});
		});
		return () => {
			unregister();
		};
	}, [editor]);

	return (
		<div className={`flex px-1.5 pt-1 gap-2 ${!showTopToolbar ? 'hidden' : ''}`}>
			<ToolbarButtons formatState={formatState} applyFormat={applyFormat} />
		</div>
	);
}

export function BottomToolbar() {
	const { showTopToolbar, editor, setShowTopToolbar, sendDisabled, submitEditor, addAttachment, commentEdit, onCancel } = useEditorContext();

	const fileInputClickHandler = (event) => {
		// create a hidden file input element
		const fileInput = document.createElement('input');
		fileInput.type = 'file';
		fileInput.multiple = true; // allow multiple file selection
		fileInput.style.display = 'none';
		fileInput.accept = ".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.csv";
		fileInput.addEventListener('change', (e) => {
			const files = Array.from(e.target.files);
			files.forEach(file => {
				addAttachment(file);
			});
			fileInput.value = ""; // reset input
		});
		document.body.appendChild(fileInput);
		fileInput.click();
		document.body.removeChild(fileInput);
	}

	const handleMentionClick = () => {
		editor.update(() => {
			const selection = $getSelection();

			if ($isRangeSelection(selection)) {
				const anchorNode = selection.anchor.getNode();
				const anchorOffset = selection.anchor.offset;
				const text = anchorNode.getTextContent();

				// Check if the character immediately before the cursor is '@'
				if (anchorOffset > 0 && text[anchorOffset - 1] === "@") {
					// Delete the existing '@'
					anchorNode.deleteText(anchorOffset - 1, anchorOffset);
				} else if (anchorOffset > 0 && text[anchorOffset - 1] !== " ") {
					// Insert a space if previous character is not a space
					selection.insertText(" ");
				}

				// Insert the new '@'
				selection.insertText("@");
			}
		});
	};


	return (
		<div className="flex items-center gap-2 px-2 pr-1 mb-1">
			<ToolbarButton
				active={showTopToolbar}
				onClick={() => setShowTopToolbar(prev => !prev)}
				title="Show Formatting Options"
			>
				format-text
			</ToolbarButton>
			<ToolbarDivider />
			{
				!commentEdit && <ToolbarButton
					active={false}
					onClick={fileInputClickHandler}
					title="Attach files upto 20MB"
				>
					attachment
				</ToolbarButton>
			}
			{/* <ToolbarButton
				active={false}
				onClick={() => { }}
				title="Voice/Audio"
			>
				mic
			</ToolbarButton> */}
			{/* <ToolbarDivider /> */}
			<ToolbarButton
				active={false}
				onClick={handleMentionClick}
				title="Mention Someone"
			>
				mention
			</ToolbarButton>
			{/* <ToolbarButton
				active={false}
				onClick={() => {}}
				title="Emoji Picker"
			>
				sentiment_very_satisfied
			</ToolbarButton> */}
			{!commentEdit &&
				<>
					<span className={`text-xs text-grey-text ml-auto transition-all ${sendDisabled ? 'opacity-0' : 'opacity-100'}`}  >
						Shift + Enter
					</span>

					<button className={`btn btn-primary btn-round p-0 h-6 w-6 hover:text-white ${sendDisabled && 'disabled'}`} onClick={submitEditor}>
						<span className="font-MaterialIconsNew text-lg">send</span>
					</button>
				</>
			}
			{
				commentEdit &&
				<div className="ml-auto flex items-center gap-2 mb-1">
					<button className="btn btn-outline-secondary p-1 px-3 h-5" onClick={() => onCancel && onCancel()}>
						Cancel
					</button>
					<button className={`btn btn-primary h-5 p-1 px-3 ${sendDisabled && 'disabled'}`} onClick={submitEditor}>
						Save
					</button>
				</div>
			}
		</div>
	);
}